import { type FC, useEffect, useRef, useState } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect, type ConnectedProps } from "react-redux";
import { SearchInput } from "../../../../../../../components";

import UsersListContainer from "./UsersListContainer/UsersListContainer";
import Restricted from "../../../../../../Application/Restricted";

import { AssignmentPeopleContext, RolePermissions, SortingDirection } from "../../../../../../../enums";
import { Columns, ColumnToParamMap, getColumnOptions } from "./getColumnOptions";
import { type AppDispatch, type RootState } from "../../../../../../Application/globaltypes/redux";
import { DEFAULT_REQUEST_PARAMS } from "../../../../../PeopleAssignments/Overview/helpers/constants";
import { fetchVideoUsers } from "../../../../state/thunks/videoUsersThunk";
import { resetSelectedItems } from "../../../../state/slices/videoUsersSlice";
import { PeopleAssignmentColumns } from "../../../../../PeopleAssignments/Overview/helpers/getColumnOptions";
import { setSearch as setSearchAction } from "../../../../../PeopleAssignments/state/actions/userAssignmentActions";
import { type FiltersMap } from "../../../../../../../utils/filterUtils";
import ContentUsersNoResults from "../../../../../Common/Fields/ContentUsersNoResults";
import { videosStateSelector } from "../../../../state/selectors";
import { useContentAssignmentsPermissions } from "../../../../../../../hooks/useContentAssignmentsPermissions";

export interface UsersOverviewPropsBase {
  videoId: number;
  customHeaderContent: React.ReactElement;
}

export type UsersOverviewProps = UsersOverviewPropsBase & PropsFromRedux;

export const UsersOverview: FC<UsersOverviewProps> = ({ videoId, customHeaderContent, fetchUsers, onUnmount }) => {
  const reloadListItems = useRef<(enableSorting: boolean) => void>();
  const [search, setSearch] = useState<string>("");

  const renderSearchInput = (): React.ReactElement => (
    <SearchInput placeholder="Search for People..." onChange={onSearchChange} defaultValue={search} />
  );

  useEffect(
    () => () => {
      onUnmount();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const createReloadListItems = (reloadListItemsFunc: (enableSorting: boolean) => void) => {
    reloadListItems.current = reloadListItemsFunc;
  };

  const onSearchChange = async (searchTerm: string) => {
    setSearch(searchTerm);
    reloadListItems.current?.(!searchTerm);
  };

  const getVideoUsers = (
    skip: number = DEFAULT_REQUEST_PARAMS.skip,
    top: number = DEFAULT_REQUEST_PARAMS.top,
    orderBy: string = PeopleAssignmentColumns.Name,
    sortingDirection: SortingDirection = DEFAULT_REQUEST_PARAMS.sortingDirection,
    appliedFilter: FiltersMap = DEFAULT_REQUEST_PARAMS.appliedFilter,
  ): void => {
    const sortingColumn = ColumnToParamMap[orderBy.toLowerCase() as Lowercase<PeopleAssignmentColumns>];
    fetchUsers({ entityId: videoId, skip, top, sortingColumn, sortingDirection, filter: appliedFilter, term: search });
  };

  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.User,
    RolePermissions.AssetsManage,
  );

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={(hasPermission) => (
        <UsersListContainer
          defaultSortingColumnName={Columns.Name}
          sortingDirection={SortingDirection.Ascending}
          columnOptions={getColumnOptions()}
          applyFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          resetFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          fetchData={getVideoUsers}
          renderSearch={renderSearchInput}
          customHeaderContent={customHeaderContent}
          isSelectDisabled={() => true}
          setReloadListItems={createReloadListItems}
          noResultsContent={<ContentUsersNoResults filtered={!!search} />}
        />
      )}
    />
  );
};
/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const edit = videosStateSelector(state).edit;
  return {
    selectedIds: edit.users.selectedIds,
    search: state.library.userAssignment.search,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchUsers: bindActionCreators(fetchVideoUsers, dispatch),
  setSearch: bindActionCreators(setSearchAction, dispatch),
  onUnmount: () => dispatch(resetSelectedItems()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersOverview);
export default ConnectedComponent;
